/**
 * The Height breach report's PDF in Teslyn Cloud (MB's decision 1034, Q900 A; R219; 20261104237000_breach_report_pdf.sql):
 * each time a Teslyn Field laptop writes the report it sends the PDF at the next sync; Teslyn Cloud keeps the latest
 * one per project. "Open breach report" opens it in a new tab through a 5-minute signed link: in the Safety register
 * for the project's staff, and on the client portal's Safety page for a client while at least one of the project's
 * breaches is shown to them (the database decides, `breach_report_pdf` and the bucket's policy).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { BREACH_REPORT_BUCKET } from '@teslyn/domain';
import { missingView, plainPortalError } from '../portal/api.ts';

/** Where the project's latest report PDF is. */
export interface BreachReportPdf {
  readonly bucket: string;
  readonly object_path: string;
  readonly file_name: string;
  readonly size_bytes: number;
  readonly uploaded_at: string;
}

/**
 * The project's latest report PDF, or null: none sent yet, not the caller's to read, or Teslyn Cloud without the
 * update (20261104237000 not applied).
 */
export async function loadBreachReportPdf(
  sb: SupabaseClient,
  projectId: string,
): Promise<BreachReportPdf | null> {
  const { data, error } = (await sb.rpc('breach_report_pdf', { p_project: projectId })) as {
    data: unknown;
    error: { code?: string; message: string } | null;
  };
  if (missingView(error) || /breach_report_pdf/.test(error?.message ?? '')) return null;
  if (error) throw new Error(plainPortalError(error));
  const r = data as Partial<BreachReportPdf> | null;
  return r && typeof r.object_path === 'string' && r.object_path
    ? {
        bucket: BREACH_REPORT_BUCKET,
        object_path: r.object_path,
        file_name: r.file_name ?? '',
        size_bytes: Number(r.size_bytes ?? 0),
        uploaded_at: r.uploaded_at ?? '',
      }
    : null;
}

/** A 5-minute link to the PDF, opened in the browser's own viewer. */
export async function breachReportUrl(sb: SupabaseClient, pdf: BreachReportPdf): Promise<string> {
  const { data, error } = await sb.storage.from(BREACH_REPORT_BUCKET).createSignedUrl(pdf.object_path, 300);
  if (error || !data)
    throw new Error(error ? plainPortalError(error) : 'The breach report could not be opened.');
  return data.signedUrl;
}

/** Open an address in a new tab (else in this one, when the browser blocks the tab). */
export const openInNewTab = (url: string): void => {
  const tab = window.open(url, '_blank', 'noopener');
  if (!tab) window.location.assign(url);
};
